import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { map } from 'rxjs/operators';
import BudgetType from '../../../../../enums/budget-type';
import ClassifierGroup from '../../../../../enums/classifier-group';
import Classifier from '../../../../../enums/classifier';
import ClassifierPropertyKey from '../../../../../enums/classifier-property-key';
import { useService } from '../../../../../services/services';
import {
  Option,
  SelectController,
} from '../../../../../components/form/elements/select';
import removeImage from './remove.svg';
import css from '../../index.module.css';

interface AddButtonProps {
  categories: Array<Option>;
  addCategory: (val: Option) => void;
  revenuesName?: string;
}

export interface ICategories {
  [key: string]: string;
}

export default function AddButton({
  categories,
  addCategory,
  revenuesName = BudgetType.REVENUES_CROP,
}: AddButtonProps) {
  const { t } = useTranslation();
  const { classifierService } = useService();
  const [isAdding, setIsAdding] = useState(false);

  const watchValues = useWatch({ name: 'add-category', disabled: false });

  useEffect(() => {
    function onChange(val: Option) {
      setIsAdding(false);
      addCategory && addCategory(val);
    }

    if (watchValues) {
      if (watchValues && watchValues.value) {
        onChange(watchValues);
      }
    }
  }, [addCategory, watchValues]);

  function addSelect() {
    setIsAdding(true);
  }

  function removeSelect() {
    setIsAdding(false);
  }

  function transform(response: any) {
    return revenuesName === BudgetType.REVENUES_CROP
      ? {
          hasMore: response.last_page > response.current_page,
          options: response.data.map((obj: any) => ({
            value: obj.id,
            label: obj.value,
          })),
          additional: { page: response.current_page + 1 },
        }
      : {
          hasMore: response.last_page > response.current_page,
          options: response.data.map((obj: any) => {
            let animal_type: string = null;
            Object.keys(obj.properties).forEach((key) => {
              if (
                obj.properties[key]?.property?.key ===
                ClassifierPropertyKey.ANIMAL_TYPE
              ) {
                animal_type = t(
                  'pages.finance.budget.animal_type.' +
                    obj.properties[key].value
                );
              }
            });
            return {
              value: obj.id,
              label: animal_type
                ? obj.value + ' (' + animal_type + ')'
                : obj.value,
            };
          }),
          additional: { page: response.current_page + 1 },
        };
  }

  const asyncOptions = (value: string, _: Option[], { page = 1 }: any) => {
    if (revenuesName === BudgetType.REVENUES_CROP) {
      return classifierService
        .values(
          ClassifierGroup.WAREHOUSE,
          Classifier.PRODUCT_CATEGORIES_AND_TYPES,
          { without_not_applicable: true, page, value }
        )
        .pipe(map(transform));
    } else {
      return classifierService
        .values(ClassifierGroup.LIVESTOCK, Classifier.ANIMALS, { page, value })
        .pipe(map(transform));
    }
  };

  return (
    <div
      className={`${css['table-row']} ${css['row-lvl-2']} ${css['no-border']} ${css['no-hover']}`}
    >
      <div className={`${css['table-data']} ${css['add-button-container']}`}>
        {isAdding ? (
          <div className={css['select-container']}>
            <SelectController
              multi={false}
              disabled={false}
              asyncOptions={asyncOptions}
              options={[]}
              cacheOn={[]}
              name={'add-category'}
              parentName={null}
              clearable={true}
            />
          </div>
        ) : categories && categories.length ? (
          <div
            className={css['add-button']}
            onClick={addSelect}
            data-name={'add-button'}
          >
            {t('pages.finance.budget.add_button')}
          </div>
        ) : null}
      </div>

      <div className={css['table-data']}></div>

      <div className={`${css['table-data']} ${css['add-button-container']}`}>
        {isAdding ? (
          <span className={css['add-button-remove']} onClick={removeSelect}>
            <img src={removeImage} alt="" />
          </span>
        ) : null}
      </div>
    </div>
  );
}
